import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { useUserProperties, useSetUserPropertyValue, useUpdateUserPropertyValue, useDeleteUserPropertyValue, usePropertyDefinitions, usePropertyDefinition } from '../hooks/useQueries'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { AppIcon } from './ui/icon'
import { mdiCheck, mdiClose, mdiDelete, mdiLock, mdiPlus } from '@mdi/js'
import { getBooleanOptions, getPropertyConstraints, EnumPropertyInput } from './properties/PropertyInputUtils'
import { PropertyValueDisplay, PropertyValueEditInput, usesDialogEditor, type PropertySibling } from './properties/PropertyValueCell'
import { useCan } from '../contexts/PermissionsContext'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog'
import { apiClient } from '../api/client'
import { EmptyState } from './ui/empty-state'

interface UserPropertiesProps {
    org: string
    userUid: string
    onPropertyUpdated?: () => void
    // Optional section label override. Falls back to the generic
    // `user_properties.title` ("Properties") key. Lets callers group this same
    // click-to-edit property table under a more specific heading (e.g.
    // "Coordinates" on the contact detail page) without forking the component.
    title?: string
    // Property codes to omit from both the displayed list and the "Add
    // property" picker. The party page's identity card renders `phone` as its
    // own first-class row (spec 2026-09-10-02) and passes `['phone']` here so
    // the number is not shown twice.
    hiddenCodes?: string[]
}

export function UserProperties({ org, userUid, onPropertyUpdated, title, hiddenCodes = [] }: UserPropertiesProps) {
    const { t: tCommon } = useTranslation('common')
    const { t } = useTranslation('users')
    // Properties state
    const [showAddPropertyForm, setShowAddPropertyForm] = useState(false)
    const [newPropertyDefinitionUid, setNewPropertyDefinitionUid] = useState('')
    const [newPropertyValue, setNewPropertyValue] = useState('')
    const [addPropertyError, setAddPropertyError] = useState<string | null>(null)
    const [editingPropertyCode, setEditingPropertyCode] = useState<string | null>(null)
    const [editingPropertyValue, setEditingPropertyValue] = useState('')
    const [propertyEditErrors, setPropertyEditErrors] = useState<Record<string, string>>({})

    // Load user properties (flat map)
    const { data: propertiesMap = {}, isLoading: propertiesLoading } = useUserProperties(org, userUid)

    // Load property definitions filtered for user target
    const { data: userPropertyDefs = [] } = usePropertyDefinitions(org, { entityType: 'user' })

    // Build display list: [{code, value, propertyDefinition}, ...]
    const propertyEntries = useMemo(() =>
        Object.entries(propertiesMap)
            .filter(([code]) => !hiddenCodes.includes(code))
            .map(([code, value]) => ({
                code,
                value,
                propertyDefinition: userPropertyDefs.find(pd => pd.code === code)
            })),
        [propertiesMap, userPropertyDefs, hiddenCodes]
    )

    // Property mutations
    const setPropertyValueMutation = useSetUserPropertyValue(org, userUid)
    const updatePropertyValueMutation = useUpdateUserPropertyValue(org, userUid)
    const deletePropertyValueMutation = useDeleteUserPropertyValue(org, userUid)

    // Helper functions
    const getPropertyDefinitionByCode = useCallback((code: string) => {
        return userPropertyDefs.find(pd => pd.code === code)
    }, [userPropertyDefs])

    const getPropertyName = useCallback((code: string) => {
        const propDef = getPropertyDefinitionByCode(code)
        if (!propDef) return code

        if (typeof propDef.name === 'object' && propDef.name !== null) {
            const names = propDef.name as Record<string, string>
            return names.en || names.fr || Object.values(names)[0] || propDef.code || code
        }

        return propDef.name || propDef.code || code
    }, [getPropertyDefinitionByCode])

    // Property definition search for add form
    const propertyDefinitionInitialOptions: ApiSearchableSelectOption[] = useMemo(() =>
        userPropertyDefs
            .filter(pd => !pd.flags?.includes('system_managed'))
            .filter(pd => !hiddenCodes.includes(pd.code))
            .map(pd => {
                const name = typeof pd.name === 'object' && pd.name !== null
                    ? (pd.name as Record<string, string>).en || (pd.name as Record<string, string>).fr || Object.values(pd.name)[0] || pd.code || pd.uid
                    : pd.name || pd.code || pd.uid
                const flags = []
                if (pd.data_type === 'enum' && pd.properties?.multiple === true) {
                    flags.push('Multiple')
                }
                if ((pd.data_type === 'boolean' || pd.data_type === 'enum') && pd.flags?.includes('attribute')) {
                    flags.push('Attribute')
                }
                const flagsStr = flags.length > 0 ? ` [${flags.join(', ')}]` : ''
                return {
                    value: pd.uid || '',
                    label: `${name} (${pd.data_type})${flagsStr}`
                }
            }),
        [userPropertyDefs, hiddenCodes]
    )

    const searchPropertyDefinitions = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        const filtered = propertyDefinitionInitialOptions.filter(option =>
            option.label.toLowerCase().includes(query.toLowerCase())
        )
        return filtered.slice(0, 50)
    }, [propertyDefinitionInitialOptions])

    // Selected property definition for add form
    const { data: selectedPropertyDef } = usePropertyDefinition(org, newPropertyDefinitionUid)

    // Property editing handlers
    // Parties (the /users surface) are the `contacts` resource.
    const canWriteContacts = useCan('contacts.write')

    const startEditingProperty = useCallback((code: string, value: unknown) => {
        setEditingPropertyCode(code)

        const propDef = getPropertyDefinitionByCode(code)
        let initialValue = ''

        if (propDef?.data_type === 'enum' && propDef.properties?.multiple === true && Array.isArray(value)) {
            initialValue = value.join(', ')
        } else if (value !== null && value !== undefined) {
            initialValue = String(value)
        }

        if (propDef?.data_type === 'date' && initialValue) {
            try {
                const date = new Date(initialValue)
                if (!isNaN(date.getTime())) {
                    if (propDef.properties?.with_time === true) {
                        const year = date.getFullYear()
                        const month = String(date.getMonth() + 1).padStart(2, '0')
                        const day = String(date.getDate()).padStart(2, '0')
                        const hours = String(date.getHours()).padStart(2, '0')
                        const minutes = String(date.getMinutes()).padStart(2, '0')
                        initialValue = `${year}-${month}-${day}T${hours}:${minutes}`
                    } else {
                        const year = date.getFullYear()
                        const month = String(date.getMonth() + 1).padStart(2, '0')
                        const day = String(date.getDate()).padStart(2, '0')
                        initialValue = `${year}-${month}-${day}`
                    }
                }
            } catch {
                // Keep original value
            }
        }

        setEditingPropertyValue(initialValue)
        setPropertyEditErrors({})
    }, [getPropertyDefinitionByCode])

    const handleCancelPropertyEdit = useCallback(() => {
        setEditingPropertyCode(null)
        setEditingPropertyValue('')
        setPropertyEditErrors({})
    }, [])

    // `value` is `unknown`, not `string`: object-valued properties (ISO 20022 address, JSON)
    // come straight out of their dialog editors and must be passed through untouched.
    const processValue = useCallback((propDef: any, value: unknown): any => {
        if (!propDef || typeof value !== 'string') return value

        // Type coercions only — validation is delegated to the backend
        switch (propDef.data_type) {
            case 'boolean':
                return value === 'true'
            case 'date':
                if (value && !isNaN(Date.parse(value))) {
                    if (propDef.properties?.with_time === true) {
                        const dateValue = new Date(value)
                        return dateValue.toISOString()
                    } else {
                        const dateValue = new Date(value)
                        return dateValue.toISOString().split('T')[0]
                    }
                } else if (value) {
                    throw new Error('Please enter a valid date')
                }
                return value
            case 'enum':
                if (propDef.properties?.multiple === true) {
                    return value ? value.split(',').map((v: string) => v.trim()).filter((v: string) => v) : []
                }
                return value
            case 'number':
                if (value !== '') {
                    const numValue = parseFloat(value)
                    if (isNaN(numValue)) {
                        throw new Error('Please enter a valid number')
                    }
                    return numValue
                }
                return value
            default:
                return value
        }
    }, [])

    const handleSavePropertyValue = useCallback(async (code: string, value: unknown) => {
        try {
            const propDef = getPropertyDefinitionByCode(code)
            const processedValue = processValue(propDef, value)

            // Delegate validation to the backend if we have a property definition UID
            if (propDef?.uid) {
                let finalValue: any = processedValue
                try {
                    const validation = await apiClient.POST('/api/v1/organizations/{orgCode}/properties/{propertyUid}/validate', {
                        params: { path: { orgCode: org, propertyUid: propDef.uid } },
                        body: { value: processedValue }
                    })
                    if (validation.data && validation.data.status !== 'ok') {
                        setPropertyEditErrors(prev => ({ ...prev, [code]: (validation.data as any).errors?.join('; ') || 'Validation failed' }))
                        return
                    }
                    if (validation.data?.value !== undefined) {
                        finalValue = validation.data.value
                    }
                } catch {
                    // Validation endpoint unavailable — proceed with save
                }

                await updatePropertyValueMutation.mutateAsync({
                    propertyCode: code,
                    value: finalValue
                })
            } else {
                await updatePropertyValueMutation.mutateAsync({
                    propertyCode: code,
                    value: processedValue
                })
            }

            setEditingPropertyCode(null)
            setEditingPropertyValue('')
            setPropertyEditErrors({})
            onPropertyUpdated?.()
        } catch (error) {
            const errorMessage = (error as any)?.title || (error as any)?.message || 'Failed to update property'
            setPropertyEditErrors({ [code]: errorMessage })
        }
    }, [updatePropertyValueMutation, getPropertyDefinitionByCode, processValue, onPropertyUpdated, org])

    // Sibling property list handed to the shared value cell. `address_iso20022` does not
    // target users today, but when it does the trio writes resolve through these entries.
    const propertySiblings: PropertySibling[] = useMemo(() =>
        propertyEntries.map((e): PropertySibling => ({ code: e.code, key: e.code, value: e.value })),
        [propertyEntries]
    )

    const getPropertyDisplayElement = useCallback((value: unknown, code?: string) => {
        if (value === null || value === undefined) return null

        return (
            <PropertyValueDisplay
                org={org}
                value={value}
                propertyDefinition={code ? getPropertyDefinitionByCode(code) : undefined}
                propertyKey={code || ''}
                siblings={propertySiblings}
                readonly={!canWriteContacts}
                saveProperty={handleSavePropertyValue}
            />
        )
    }, [getPropertyDefinitionByCode, org, propertySiblings, handleSavePropertyValue, canWriteContacts])

    const handleDeletePropertyValue = useCallback(async (code: string) => {
        try {
            await deletePropertyValueMutation.mutateAsync(code)
            onPropertyUpdated?.()
        } catch (error) {
            console.error('Failed to delete property value:', error)
        }
    }, [deletePropertyValueMutation, onPropertyUpdated])

    const handlePropertyKeyDown = useCallback((e: React.KeyboardEvent, code: string, value: string) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSavePropertyValue(code, value)
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancelPropertyEdit()
        }
    }, [handleSavePropertyValue, handleCancelPropertyEdit])

    // Add property handlers
    const handlePropertyDefinitionSelect = useCallback((option: ApiSearchableSelectOption) => {
        setNewPropertyDefinitionUid(option.value)
        setNewPropertyValue('')
        setAddPropertyError(null)
    }, [])

    const handleAddProperty = useCallback(async () => {
        if (!newPropertyDefinitionUid) return

        try {
            const selectedPropDef = userPropertyDefs.find(pd => pd.uid === newPropertyDefinitionUid)
            if (!selectedPropDef?.code) {
                setAddPropertyError('Selected property definition has no code')
                return
            }

            // Validate required value for boolean
            if (selectedPropDef.data_type === 'boolean' && !newPropertyValue) {
                setAddPropertyError('Please select a boolean value')
                return
            }
            if (selectedPropDef.data_type === 'date' && Array.isArray(newPropertyValue)) {
                setAddPropertyError('Multiple values not supported for date type')
                return
            }
            if (selectedPropDef.data_type === 'date' && (!newPropertyValue || isNaN(Date.parse(newPropertyValue)))) {
                setAddPropertyError('Please enter a valid date')
                return
            }

            const processedValue = processValue(selectedPropDef, newPropertyValue)

            // Validate with backend before saving
            if (selectedPropDef.uid) {
                try {
                    const validation = await apiClient.POST('/api/v1/organizations/{orgCode}/properties/{propertyUid}/validate', {
                        params: { path: { orgCode: org, propertyUid: selectedPropDef.uid } },
                        body: { value: processedValue }
                    })
                    if (validation.data && validation.data.status !== 'ok') {
                        setAddPropertyError((validation.data as any).errors?.join('; ') || 'Validation failed')
                        return
                    }
                } catch {
                    // Validation endpoint unavailable — proceed with save
                }
            }

            await setPropertyValueMutation.mutateAsync({
                propertyCode: selectedPropDef.code,
                value: processedValue
            })

            setNewPropertyDefinitionUid('')
            setNewPropertyValue('')
            setShowAddPropertyForm(false)
            setAddPropertyError(null)
            onPropertyUpdated?.()
        } catch (error) {
            const errorMessage = (error as any)?.title || (error as any)?.message || 'Failed to add property'
            setAddPropertyError(errorMessage)
        }
    }, [setPropertyValueMutation, newPropertyDefinitionUid, newPropertyValue, onPropertyUpdated, userPropertyDefs, processValue, org])

    const handleCancelAddProperty = useCallback(() => {
        setNewPropertyDefinitionUid('')
        setNewPropertyValue('')
        setShowAddPropertyForm(false)
        setAddPropertyError(null)
    }, [])

    // Render property value input for editing
    const renderEditPropertyInput = useCallback((code: string, value: string, setValue: (value: string) => void, onKeyDown: (e: React.KeyboardEvent) => void) => (
        <PropertyValueEditInput
            org={org}
            propertyDefinition={getPropertyDefinitionByCode(code)}
            value={value}
            onChange={setValue}
            onKeyDown={onKeyDown}
            disabled={updatePropertyValueMutation.isPending}
            hasError={Boolean(propertyEditErrors[code])}
        />
    ), [getPropertyDefinitionByCode, propertyEditErrors, updatePropertyValueMutation.isPending, org])

    // Render property value input for add form
    const renderPropertyValueInput = useCallback(() => {
        if (!selectedPropertyDef) return null

        if (selectedPropertyDef.data_type === 'boolean') {
            const semantic = selectedPropertyDef.properties?.semantic as string | undefined
            const options = getBooleanOptions(semantic, tCommon)
            return (
                <select
                    value={newPropertyValue}
                    onChange={(e) => setNewPropertyValue(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                    <option value="">-- Select Value --</option>
                    {options.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            )
        }

        if (selectedPropertyDef.data_type === 'date') {
            const withTime = selectedPropertyDef.properties?.with_time === true
            return (
                <input
                    type={withTime ? "datetime-local" : "date"}
                    value={newPropertyValue}
                    onChange={(e) => setNewPropertyValue(e.target.value)}
                    onFocus={(e) => (e.currentTarget as any).showPicker?.()}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
            )
        }

        if (selectedPropertyDef.data_type === 'enum') {
            return <EnumPropertyInput propertyDefinitionUid={selectedPropertyDef.uid} value={newPropertyValue} onChange={setNewPropertyValue} org={org} />
        }

        if (selectedPropertyDef.data_type === 'number') {
            return (
                <input
                    type="number"
                    value={newPropertyValue}
                    onChange={(e) => setNewPropertyValue(e.target.value)}
                    step={selectedPropertyDef.properties?.integer_only ? "1" : "any"}
                    min={typeof selectedPropertyDef.properties?.min_value === 'number' ? selectedPropertyDef.properties.min_value : undefined}
                    max={typeof selectedPropertyDef.properties?.max_value === 'number' ? selectedPropertyDef.properties.max_value : undefined}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder={`Enter ${selectedPropertyDef.properties?.integer_only ? 'integer' : 'number'}${selectedPropertyDef.properties?.unit ? ` (${selectedPropertyDef.properties.unit})` : ''}...`}
                />
            )
        }

        if (selectedPropertyDef.data_type === 'text') {
            const hasValidationError = selectedPropertyDef.properties?.regex_pattern && newPropertyValue && (() => {
                try {
                    const regex = new RegExp(selectedPropertyDef.properties.regex_pattern as string)
                    return !regex.test(newPropertyValue)
                } catch {
                    return false
                }
            })()

            return (
                <div>
                    <input
                        type="text"
                        value={newPropertyValue}
                        onChange={(e) => setNewPropertyValue(e.target.value)}
                        className={`flex h-9 w-full rounded-md border ${hasValidationError ? 'border-destructive' : 'border-input'} bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
                        placeholder="Enter text..."
                    />
                    {hasValidationError ? (
                        <p className="text-xs text-destructive mt-1">
                            Pattern: {(selectedPropertyDef.properties?.regex_pattern as string) || ''}
                        </p>
                    ) : null}
                </div>
            )
        }

        return (
            <input
                type="text"
                value={newPropertyValue}
                onChange={(e) => setNewPropertyValue(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Enter value..."
            />
        )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPropertyDef, newPropertyValue, org])

    return (
        <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-foreground" data-testid="user-properties-title">{title || t('user_properties.title')}</h3>
                {canWriteContacts && (
                    <Button
                        onClick={() => setShowAddPropertyForm(!showAddPropertyForm)}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 text-sm"
                        aria-label={t('user_properties.add_property')}
                        title={t('user_properties.add_property')}
                        data-testid="add-user-property-btn"
                    >
                        <AppIcon path={mdiPlus} size="md" className="md:mr-1" />
                        <span className="hidden md:inline">{t('user_properties.add_property')}</span>
                    </Button>
                )}
            </div>

            {/* Add Property Form */}
            {showAddPropertyForm && (
                <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
                    <h4 className="text-md font-medium text-foreground mb-3">{t('user_properties.add_new_property')}</h4>
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                {t('user_properties.property_definition')}
                            </label>
                            <ApiSearchableSelect
                                value={newPropertyDefinitionUid}
                                onValueChange={setNewPropertyDefinitionUid}
                                onOptionSelect={handlePropertyDefinitionSelect}
                                onSearch={searchPropertyDefinitions}
                                placeholder={t('user_properties.select_property')}
                                searchPlaceholder={t('user_properties.search_property')}
                                emptyMessage={t('user_properties.no_definitions')}
                                initialOptions={propertyDefinitionInitialOptions}
                                className="w-full"
                                data-testid="user-property-definition-select"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                {t('user_properties.value')}
                            </label>
                            {renderPropertyValueInput()}
                        </div>
                    </div>
                    {addPropertyError && (
                        <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                            <p className="text-sm text-destructive">{addPropertyError}</p>
                        </div>
                    )}
                    <div className="flex gap-2 mt-4">
                        <Button
                            onClick={handleAddProperty}
                            disabled={setPropertyValueMutation.isPending || !newPropertyDefinitionUid}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="save-user-property-btn"
                        >
                            {setPropertyValueMutation.isPending ? t('user_properties.adding') : t('user_properties.add_property')}
                        </Button>
                        <Button
                            onClick={handleCancelAddProperty}
                            disabled={setPropertyValueMutation.isPending}
                            variant="outline"
                            className="px-4 py-2 text-sm"
                            data-testid="cancel-add-user-property-btn"
                        >
                            {t('user_properties.cancel')}
                        </Button>
                    </div>
                </div>
            )}

            {propertiesLoading ? (
                <div className="text-muted-foreground">{t('user_properties.loading')}</div>
            ) : propertyEntries.length === 0 ? (
                <EmptyState variant="inline" title={t('user_properties.empty')} />
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                    {t('user_properties.column_property')}
                                </th>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                    {t('user_properties.column_value')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-card divide-y divide-border">
                            {propertyEntries.map((entry) => (
                                <tr key={entry.code} className="hover:bg-muted/50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                        <div>
                                            <div className="flex items-center gap-1">
                                                {getPropertyName(entry.code)}
                                                {entry.propertyDefinition?.flags?.includes('system_managed') && (
                                                    <span title="System managed">
                                                        <AppIcon tone="muted" path={mdiLock} size="xs" />
                                                    </span>
                                                )}
                                            </div>
                                            {(() => {
                                                const constraints = getPropertyConstraints(entry.propertyDefinition, tCommon)
                                                return constraints.length > 0 ? (
                                                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                                                        {constraints.join(' \u2022 ')}
                                                    </div>
                                                ) : null
                                            })()}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                        {editingPropertyCode === entry.code ? (
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    {renderEditPropertyInput(
                                                        entry.code,
                                                        editingPropertyValue,
                                                        setEditingPropertyValue,
                                                        (e) => handlePropertyKeyDown(e, entry.code, editingPropertyValue)
                                                    )}
                                                    <Button
                                                        onClick={() => handleSavePropertyValue(entry.code, editingPropertyValue)}
                                                        disabled={updatePropertyValueMutation.isPending}
                                                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-2 py-1 text-sm"
                                                        data-testid="save-property-value"
                                                    >
                                                        {updatePropertyValueMutation.isPending ? '...' : <AppIcon path={mdiCheck} size="xs" />}
                                                    </Button>
                                                    <Button
                                                        onClick={handleCancelPropertyEdit}
                                                        disabled={updatePropertyValueMutation.isPending}
                                                        variant="outline"
                                                        className="px-2 py-1 text-sm"
                                                        data-testid="cancel-property-edit"
                                                    >
                                                        <AppIcon path={mdiClose} size="xs" />
                                                    </Button>
                                                </div>
                                                {propertyEditErrors[entry.code] && (
                                                    <p className="mt-1 text-xs text-destructive">{propertyEditErrors[entry.code]}</p>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between group">
                                                <span
                                                    className={!canWriteContacts || entry.propertyDefinition?.flags?.includes('system_managed') || usesDialogEditor(entry.propertyDefinition, entry.value)
                                                        ? 'cursor-default p-1 rounded transition-colors flex-1'
                                                        : 'cursor-pointer hover:bg-muted/50 p-1 rounded transition-colors flex-1'}
                                                    onClick={() => {
                                                        if (!canWriteContacts) return
                                                        // Object-valued properties are edited through their own dialog
                                                        // (address / JSON) — never seed one into the inline text input.
                                                        if (!entry.propertyDefinition?.flags?.includes('system_managed') && !usesDialogEditor(entry.propertyDefinition, entry.value)) {
                                                            startEditingProperty(entry.code, entry.value)
                                                        }
                                                    }}
                                                    title={entry.propertyDefinition?.flags?.includes('system_managed') ? 'This property is system-managed' : 'Click to edit'}
                                                    data-testid="edit-property-value"
                                                >
                                                    {getPropertyDisplayElement(entry.value, entry.code) || (entry.propertyDefinition?.flags?.includes('system_managed') ? 'N/A' : 'Click to edit')}
                                                </span>
                                                {canWriteContacts && !entry.propertyDefinition?.flags?.includes('system_managed') && (
                                                    <AlertDialog>
                                                        <AlertDialogTrigger asChild>
                                                            <Button
                                                                disabled={deletePropertyValueMutation.isPending}
                                                                variant="ghost"
                                                                size="sm"
                                                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 h-auto text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                title="Delete property value"
                                                                data-testid="delete-property-value"
                                                            >
                                                                <AppIcon path={mdiDelete} size="xs" />
                                                            </Button>
                                                        </AlertDialogTrigger>
                                                        <AlertDialogContent>
                                                            <AlertDialogHeader>
                                                                <AlertDialogTitle>Delete Property</AlertDialogTitle>
                                                                <AlertDialogDescription>
                                                                    Are you sure you want to delete the property "{getPropertyName(entry.code)}"? This action cannot be undone.
                                                                </AlertDialogDescription>
                                                            </AlertDialogHeader>
                                                            <AlertDialogFooter>
                                                                <AlertDialogCancel data-testid="cancel-delete-property">Cancel</AlertDialogCancel>
                                                                <AlertDialogAction
                                                                    onClick={() => handleDeletePropertyValue(entry.code)}
                                                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                                    data-testid="confirm-delete-property"
                                                                >
                                                                    Delete
                                                                </AlertDialogAction>
                                                            </AlertDialogFooter>
                                                        </AlertDialogContent>
                                                    </AlertDialog>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
